import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { TimeSlot } from '@adornis/buchungsheld-db/datetime/time-slot.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { confirmDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { useGeneratedForm } from '@adornis/generated-form/use-generated-form.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { searchParamsSignal } from '@adornis/router/client/routing-state-signal.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { withFormAutofiller } from '../../components/form-autofiller.js';
import { addInterviewSlots, getJobPositionByID } from '../../../api/job-position.js';
import { subscribeJobApplicationsWithFilter } from '../../../api/job-application.js';
import { JobApplicationStatus } from '../../../db/job-application.js';
import { getMatchingWeekCategoryByID } from '../../../api/category.js';
import { MatchingWeekCategory } from '../../../db/category.js';
import {
  EmploymentType,
  ExpectedAcademicTitle,
  JobPosition,
  PreferredStudyField,
  SELECTABLE_SPEECH_LEVELS,
  SoftSkills,
} from '../../../db/job-position.js';
import { enumRenderer as makeEnumRenderer } from '../../components/enum-renderer.js';
import { formatFormError } from '../../components/format-form-error.js';
import { labeledField } from '../../components/labeled-field.js';
import { MultilanguageFormattableTextarea } from '../../components/multilanguage-formattable-textarea.js';
import { TimeSlotsDisplay } from '../../components/time-slots-display.js';
import { companyJobRouter, companyRouter } from './company-routes.js';

/** Bound to this form's translation prefix; matches `job_form_enum_<VALUE>` keys. */
const enumRenderer = <E extends Record<string, string>>(enumType: E, multi = false, maxItems?: number) =>
  makeEnumRenderer(enumType, 'job_form_enum', multi, maxItems);

/**
 * Back-button helper for the job-edit flow. If the CTA that opened this page
 * passed `?returnTo=…`, navigate there; otherwise fall back to `fallbackPath`.
 * Mirrors the pattern in `company-application-detail.ts:32-36`.
 */
const goBackFromEdit = (fallbackPath: string) => {
  const returnTo = searchParamsSignal.value.get('returnTo');
  goTo({ path: returnTo ?? fallbackPath });
};

@Entity()
class GenerateTimeSlotsConfig extends AdornisEntity {
  static override _class = 'GenerateTimeSlotsConfig';

  @Field(() => Date)
  day: Maybe<Date>;

  @Field(() => Date)
  start: Maybe<Date>;

  @Field(() => Date)
  end: Maybe<Date>;

  @Field(() => Int)
  durationPerSlot: Maybe<number>;

  @Field(() => Boolean)
  hasLunchBreak: Maybe<boolean>;

  @Field(() => Date)
  lunchBreakStart: Maybe<Date>;

  @Field(() => Date)
  lunchBreakEnd: Maybe<Date>;
}

// Component for generating time slots (step 2)
const TimeSlotsGenerator = stateful((props: { job: JobPosition; onBack: () => void; onComplete: () => void }) => {
  // Load the category data when the component mounts
  const chosenCategory = useQuery(async () => {
    if (!props.job?.categoryID) return;
    try {
      const category = await getMatchingWeekCategoryByID(props.job.categoryID)({ _id: 1, name: 1, fixedEventDate: 1 });
      return category;
    } catch (e) {
      console.error('Failed to load category:', e);
    }
  }, [props.job?.categoryID]);
  const [selectedRemoveIDs, setSelectedRemoveIDs] = useState<string[]>([], [props.job._id]);
  const [saving, setSaving] = useState(false);
  const bookedApplications = useQuery(
    () =>
      subscribeJobApplicationsWithFilter(
        0,
        10000,
        new AdornisFilter({
          aspects: [
            new StringAspect({ fieldName: 'jobPositionId', value: props.job._id, mode: 'equals' }),
            new StringAspect({ fieldName: 'status', value: JobApplicationStatus.BOOKED, mode: 'equals' }),
          ],
        }),
      )({ _id: 1, interviewStart: 1, interviewEnd: 1 }),
    [props.job._id],
  );
  if (chosenCategory.loading) return 'loading';
  if (!chosenCategory.value) return 'no category, something went wrong';

  // Create config with default date from category if available
  const defaultDate = chosenCategory.value?.fixedEventDate ? new Date(chosenCategory.value.fixedEventDate) : undefined;

  // Stabilize the slotConfig reference — useGeneratedForm internally memoizes
  // the document by `[documentInput]`, so a fresh `new GenerateTimeSlotsConfig`
  // each render would otherwise reset the form state on every re-render.
  const [slotConfig] = useState(new GenerateTimeSlotsConfig({ day: defaultDate }));
  const { form: formSlotGeneration, renderField: renderFieldSlotGeneration } = useGeneratedForm(
    slotConfig,
    { translationPrefix: 'job_form_slot_generation_', validator: null },
    [chosenCategory.value?._id],
  );

  const applySlots = async (slots: TimeSlot[]) => {
    if (saving) return;
    setSaving(true);
    try {
      const result = await addInterviewSlots(
        props.job._id,
        slots,
        selectedRemoveIDs,
      )({ added: 1, skipped: 1, removed: 1 });
      const refreshedJob = await getJobPositionByID(props.job._id)({
        _id: 1,
        timeSlots: selectionSet(() => TimeSlot, 1),
      });
      if (!refreshedJob) throw new Error(m.job_form_slot_generation_refresh_error());
      props.job.timeSlots = (refreshedJob.timeSlots ?? []).map(slot => new TimeSlot(slot));
      setSelectedRemoveIDs([]);
      void XSnackbar.show(
        m.job_form_slot_generation_result({
          added: result.added,
          skipped: result.skipped,
          removed: result.removed,
        }),
      );
      props.onComplete();
    } catch (error) {
      void XSnackbar.show(error instanceof Error ? error.message : m.job_form_slot_generation_error_alert());
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSlots = async () => {
    const validationError = await formSlotGeneration.validate();
    if (validationError) {
      void XSnackbar.show(formatFormError(validationError, 'job_form_slot_generation_'));
      return;
    }
    try {
      // Read the current values picked through the generated form.
      const entity = formSlotGeneration.document;
      if (!entity) return;
      const day = entity.day;
      // Date-only values are UTC midnight; time inputs contain local hours/minutes.
      const onSelectedDay = (time: Maybe<Date>) =>
        day && time
          ? new Date(
              day.getUTCFullYear(),
              day.getUTCMonth(),
              day.getUTCDate(),
              time.getHours(),
              time.getMinutes(),
              0,
              0,
            )
          : undefined;
      const start = onSelectedDay(entity.start);
      const end = onSelectedDay(entity.end);
      const duration = entity.durationPerSlot ?? 0;

      // notify user when input is invalid
      if (!day || !start || !end || !duration || duration <= 0 || end < start) {
        void XSnackbar.show(
          !day
            ? 'Bitte Tag wählen.'
            : !start
              ? 'Bitte Startzeit wählen.'
              : !end
                ? 'Bitte Endzeit wählen.'
                : !duration
                  ? 'Bitte Termindauer eingeben.'
                  : duration <= 0
                    ? 'Dauer muss größer 0 sein.'
                    : end < start
                      ? 'Endzeit muss nach Startzeit liegen.'
                      : 'Bitte Start, Ende und Dauer prüfen.',
        );
        return;
      }

      const hasLunch = Boolean(entity.hasLunchBreak);
      const lunchStart = onSelectedDay(entity.lunchBreakStart);
      const lunchEnd = onSelectedDay(entity.lunchBreakEnd);

      const slots: TimeSlot[] = [];
      if (start && end) {
        let cursor = new Date(start);
        while (cursor < end) {
          const nextEnd = new Date(cursor.getTime() + duration * 60_000);
          if (nextEnd > end) break;

          // if lunch break is configured and this slot would overlap, skip to lunch end
          if (hasLunch && lunchStart && lunchEnd && cursor < lunchEnd && nextEnd > lunchStart) {
            cursor = new Date(lunchEnd);
            continue;
          }

          slots.push(new TimeSlot({ start: new Date(cursor), end: new Date(nextEnd) }));
          cursor = nextEnd;
        }
      }

      await applySlots(slots);
    } catch (error) {
      console.error('Failed to generate appointment slots:', error);
      void XSnackbar.show(error instanceof Error ? error.message : m.job_form_slot_generation_error_alert());
    }
  };

  // Group existing unbooked slots by day for removal selection. TimeSlot.start/end
  // are Date|undefined.
  const groupedByDay = (props.job.timeSlots ?? []).reduce(
    (acc, s) => {
      const start = s?.start ? new Date(s.start) : null;
      const end = s?.end ? new Date(s.end) : null;
      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return acc;
      const label = format(start, 'dd.MM.yyyy');
      (acc[label] ??= []).push({ _id: s._id, start, end });
      return acc;
    },
    {} as Record<string, Array<{ _id: string; start: Date; end: Date }>>,
  );
  Object.values(groupedByDay).forEach(items => items.sort((a, b) => a.start.getTime() - b.start.getTime()));

  return html`
    <div>
      <h2 class="text-xl font-bold mb-2">Termine für ${props.job.title ?? 'Stelle'} erstellen</h2>
      <p class="text-sm mb-4">Legen Sie hier die verfügbaren Zeitslots für diese Stelle fest.</p>

      ${
        Object.keys(groupedByDay).length > 0
          ? html`
              <div class="divider my-6">${m.job_form_slot_generation_existing()}</div>
              <label class="label justify-start gap-2 mb-4">
                <input
                  type="checkbox"
                  class="toggle toggle-primary"
                  .checked=${(props.job.timeSlots ?? []).every(slot => selectedRemoveIDs.includes(slot._id))}
                  @change=${(event: Event) =>
                    setSelectedRemoveIDs(
                      (event.target as HTMLInputElement).checked
                        ? (props.job.timeSlots ?? []).map(slot => slot._id)
                        : [],
                    )}
                />
                ${m.job_form_slot_generation_remove_all()}
              </label>
              <div class="flex flex-wrap gap-4 mb-6">
                ${Object.entries(groupedByDay)
                  .sort((a, b) => a[1][0]!.start.getTime() - b[1][0]!.start.getTime())
                  .map(
                    ([label, items]) => html`
                      <div class="card bg-base-200 shadow-sm">
                        <div class="card-body p-4">
                          <h3 class="card-title text-base">
                            ${m.time_slots_display_day()} <span class="font-normal">${label}</span>
                          </h3>
                          <div class="mt-2 flex flex-col gap-1">
                            ${items.map(
                              slot => html`
                                <label class="label justify-start gap-2 text-sm">
                                  <span>${format(slot.start, 'HH:mm')} - ${format(slot.end, 'HH:mm')}</span>
                                  <input
                                    type="checkbox"
                                    class="checkbox checkbox-sm"
                                    .checked=${selectedRemoveIDs.includes(slot._id)}
                                    @change=${(event: Event) =>
                                      setSelectedRemoveIDs(
                                        (event.target as HTMLInputElement).checked
                                          ? [...selectedRemoveIDs, slot._id]
                                          : selectedRemoveIDs.filter(id => id !== slot._id),
                                      )}
                                  />
                                  ${m.job_form_slot_generation_remove()}
                                </label>
                              `,
                            )}
                          </div>
                        </div>
                      </div>
                    `,
                  )}
              </div>
            `
          : nothing
      }
      ${
        (bookedApplications.value?.length ?? 0) > 0
          ? html`
              <div class="divider my-6">${m.job_form_slot_generation_booked_kept()}</div>
              <div class="mb-6">
                ${TimeSlotsDisplay({
                  timeSlots: (bookedApplications.value ?? []).flatMap(application =>
                    application.interviewStart && application.interviewEnd
                      ? [new TimeSlot({ start: application.interviewStart, end: application.interviewEnd })]
                      : [],
                  ),
                })}
              </div>
            `
          : nothing
      }

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="col-span-2">
          ${labeledField(
            m.job_form_slot_generation_day_label(),
            renderFieldSlotGeneration(['day'], props =>
              DateInput({
                ...props,
                type: 'date',
                disabled: !!chosenCategory.value?.fixedEventDate,
              }),
            ),
          )}
        </div>
        ${labeledField(
          m.job_form_slot_generation_startTime_label(),
          renderFieldSlotGeneration(['start'], props =>
            DateInput({
              ...props,
              type: 'time',
            }),
          ),
        )}
        ${labeledField(
          m.job_form_slot_generation_endTime_label(),
          renderFieldSlotGeneration(['end'], props =>
            DateInput({
              ...props,
              type: 'time',
            }),
          ),
        )}
        <div class="col-span-2">
          ${labeledField(
            m.job_form_slot_generation_durationPerSlot_label(),
            renderFieldSlotGeneration(['durationPerSlot'], props =>
              NumberInput({
                ...props,
                min: 5,
                step: 5,
                placeholderMode: 'hide',
                suffix: html`<span class="text-base-content/60">Min.</span>`,
              }),
            ),
          )}
        </div>
        <div class="col-span-2">${renderFieldSlotGeneration(['hasLunchBreak'])}</div>
        ${
          formSlotGeneration.document?.hasLunchBreak
            ? html`
                ${labeledField(
                  m.job_form_slot_generation_lunchBreakStart_label(),
                  renderFieldSlotGeneration(['lunchBreakStart'], props => DateInput({ ...props, type: 'time' })),
                )}
                ${labeledField(
                  m.job_form_slot_generation_lunchBreakEnd_label(),
                  renderFieldSlotGeneration(['lunchBreakEnd'], props => DateInput({ ...props, type: 'time' })),
                )}
              `
            : nothing
        }
      </div>

      <div class="flex justify-between mt-6">
        <button type="button" class="btn btn-ghost" @click=${props.onBack}>Zurück</button>
        <div class="flex gap-4">
          <button
            type="button"
            class="btn btn-outline"
            ?disabled=${saving || selectedRemoveIDs.length === 0}
            @click=${async () => {
              if (
                await confirmDialog(m.job_form_slot_generation_remove_confirm({ count: selectedRemoveIDs.length }), {
                  acceptText: m.job_form_slot_generation_remove_selected(),
                  declineText: m.job_form_cancel_button(),
                })
              )
                await applySlots([]);
            }}
          >
            ${m.job_form_slot_generation_remove_selected()}
          </button>
          <button type="button" class="btn btn-primary" ?disabled=${saving} @click=${handleGenerateSlots}>
            Termine generieren
          </button>
        </div>
      </div>
    </div>
  `;
});

// Main job form component (step 1)
const JobForm = stateful((props: { job: JobPosition; onsave: () => void }) => {
  if (!props.job) return nothing;

  // Track which step we're on (1: Job details, 2: Time slots)
  const [currentStep, setCurrentStep] = useState(1, []);

  useEffect(() => {
    if (searchParamsSignal.value.get('jumpToMeetings') === 'true') setCurrentStep(2);
  }, []);

  const { form, renderField, autofiller } = withFormAutofiller(
    useGeneratedForm(props.job, {
      translationPrefix: 'job_form_',
      validator: null,
    }),
  );

  const categoryID = form.document?.categoryID;
  const chosenCategory = useQuery(async () => {
    if (!categoryID) return;
    return getMatchingWeekCategoryByID(categoryID)({ _id: 1, name: 1, fixedEventDate: 1 });
  }, [categoryID]);
  const matchingDayDate =
    categoryID && chosenCategory.value?.fixedEventDate ? new Date(chosenCategory.value.fixedEventDate) : undefined;
  // Matching Day dates are date-only values stored at UTC midnight.
  const matchingDayDateValue =
    matchingDayDate && !isNaN(matchingDayDate.getTime())
      ? `${String(matchingDayDate.getUTCDate()).padStart(2, '0')}.${String(matchingDayDate.getUTCMonth() + 1).padStart(2, '0')}.${String(matchingDayDate.getUTCFullYear()).padStart(4, '0')}`
      : '';

  const handleSave = async () => {
    const scrollToTop = () => {
      const scrollContainer = document.querySelector('.drawer-content');
      if (scrollContainer) scrollContainer.scrollTop = 0;
    };
    try {
      const err = await form.validate();
      if (err) {
        void XSnackbar.show(formatFormError(err, 'job_form_'));
        scrollToTop();
        return;
      }
      if (props.job._id) {
        await props.job.save();
      } else {
        await props.job.create();
      }
      // Job postings are created ~2 months before interview dates; the
      // company will create time slots later via the "Gesprächstermine
      // anlegen" button on the jobs overview, so we don't auto-jump to
      // step 2 here. Users who want step 2 directly can still arrive
      // via ?jumpToMeetings=true (handled in the useEffect above).
      props.onsave();
    } catch (error) {
      // The form isn't currently wired with a client-side validator, so
      // `entity.save()` is what actually trips missing `nonOptional` fields.
      // Surface the specific field/reason instead of the generic snackbar.
      console.error('Failed to save job position:', error);
      void XSnackbar.show(formatFormError(error, 'job_form_'));
      scrollToTop();
    }
  };

  if (!form.document) return nothing;

  // Build the "back" target for both the TimeSlotsGenerator step and the
  // step-1 Cancel button. Both honor `?returnTo=…`; otherwise we navigate
  // to the job's detail page (or the role-appropriate list when there is
  // no detail page yet — e.g. brand-new job creation).
  const isAdminPath = window.location.pathname.startsWith('/admin/');
  const jobID = props.job._id;
  const fallbackPath = jobID
    ? isAdminPath
      ? `/admin/job/${props.job.companyID}/${jobID}`
      : `/company/job/${jobID}`
    : isAdminPath
      ? `/admin/company/${props.job.companyID}`
      : '/company/';
  const handleBack = () => goBackFromEdit(fallbackPath);

  // Show the appropriate step
  if (currentStep === 2) {
    return TimeSlotsGenerator({ job: props.job, onBack: handleBack, onComplete: () => props.onsave() });
  }

  return html`
    ${autofiller}
    <form
      @submit=${(e: Event) => {
        e.stopPropagation();
        e.preventDefault();
        void handleSave();
      }}
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        ${labeledField(m.job_form_title_label(), renderField(['title']))}
        ${labeledField(m.job_form_employmentType_label(), renderField(['employmentType'], enumRenderer(EmploymentType)))}
        ${labeledField(
          m.job_form_categoryID_label(),
          renderField(['categoryID'], props =>
            EntityPickerInput({
              ...props,
              class: MatchingWeekCategory,
              filter: new AdornisFilter({}),
              selectionSet: { _id: 1, name: 1, fixedEventDate: 1 },
              renderString: e => e?.name ?? '',
            }),
          ),
        )}
        ${labeledField(
          m.job_form_matchingDayDate_label(),
          html`<input
            type="text"
            class="input w-full"
            readonly
            aria-label=${m.job_form_matchingDayDate_label()}
            .value=${matchingDayDateValue}
            placeholder=${
              chosenCategory.loading
                ? ''
                : categoryID
                  ? m.job_form_matchingDayDate_no_date()
                  : m.job_form_matchingDayDate_none_selected()
            }
          />`,
        )}
      </div>

      <div class="divider my-4"></div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="col-span-2">
          ${labeledField(m.job_form_description_label(), renderField(['description'], MultilanguageFormattableTextarea))}
        </div>
        <div class="col-span-2">
          ${labeledField(
            m.job_form_practicalExperience_label(),
            renderField(['practicalExperience'], MultilanguageFormattableTextarea),
          )}
        </div>
        <div class="col-span-2">
          ${labeledField(
            m.job_form_additionalRequirements_label(),
            renderField(['additionalRequirements'], MultilanguageFormattableTextarea),
          )}
        </div>
      </div>

      <div class="divider my-4"></div>

      <h3 class="text-md font-bold mt-8 mb-4">${m.company_profile_requirements_title()}</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        ${labeledField(
          m.job_form_expectedAcademicTitle_label(),
          renderField(['expectedAcademicTitle'], enumRenderer(ExpectedAcademicTitle, true)),
        )}
        ${labeledField(
          m.job_form_preferredStudyField_label(),
          renderField(['preferredStudyField'], enumRenderer(PreferredStudyField, true)),
        )}
        ${labeledField(m.job_form_mandatoryTechSkills_label(), renderField(['mandatoryTechSkills'], TextareaInput))}
        ${labeledField(m.job_form_desiredTechSkills_label(), renderField(['desiredTechSkills'], TextareaInput))}
        ${labeledField(m.job_form_optionalTechSkills_label(), renderField(['optionalTechSkills'], TextareaInput))}
      </div>

      <div class="divider my-4"></div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div class="col-span-2">
          ${labeledField(m.job_form_softSkills_label(), renderField(['softSkills'], enumRenderer(SoftSkills, true, 5)))}
        </div>

        ${labeledField(
          m.job_form_languageRequirementsDE_label(),
          renderField(['languageRequirementsDE'], enumRenderer(SELECTABLE_SPEECH_LEVELS)),
        )}
        ${labeledField(
          m.job_form_languageRequirementsEN_label(),
          renderField(['languageRequirementsEN'], enumRenderer(SELECTABLE_SPEECH_LEVELS)),
        )}
        ${labeledField(m.job_form_languageRequirementsOther_label(), renderField(['languageRequirementsOther']))}
      </div>

      <div class="divider my-4"></div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        ${labeledField(m.job_form_location_label(), renderField(['location']))}
        ${labeledField(m.job_form_duration_label(), renderField(['duration']))}
        ${labeledField(m.job_form_startDate_label(), renderField(['startDate']))}
        <div class="col-span-2">${labeledField(m.job_form_meetingLink_label(), renderField(['meetingLink']))}</div>
      </div>

      <div class="divider my-4"></div>

      <div class="flex justify-end gap-4 mt-4">
        <button type="button" class="btn btn-ghost" @click=${handleBack}>${m.job_form_cancel_button()}</button>
        <button type="submit" class="btn btn-primary">${m.job_form_save_button()}</button>
      </div>

      ${
        props.job.timeSlots && props.job.timeSlots.length > 0
          ? html`
              <div class="mt-8 pt-4 border-t border-base-300">
                <h3 class="text-md font-bold mb-4">${m.job_form_available_appointments()}</h3>
                ${TimeSlotsDisplay({ timeSlots: props.job.timeSlots, jobPositionID: props.job._id })}
              </div>
            `
          : nothing
      }
    </form>
  `;
});

export const CompanyJobEdit = stateful((props: { companyID: string; jobPositionID: string }) => {
  if (!props.companyID) return 'no company';
  const job = useQuery(async () => {
    if (props.jobPositionID === 'new') {
      return new JobPosition({ companyID: props.companyID });
    } else {
      const result = (await getJobPositionByID(props.jobPositionID)({
        _id: 1,
        title: 1,
        employmentType: 1,
        description: selectionSet(() => TextMultipleLangItem, 1),
        location: 1,
        duration: 1,
        availableSlots: 1,
        startDate: 1,
        companyID: 1,
        expectedAcademicTitle: 1,
        preferredStudyField: 1,
        languageRequirements: 1,
        mandatoryTechSkills: 1,
        desiredTechSkills: 1,
        optionalTechSkills: 1,
        softSkills: 1,
        categoryID: 1,
        practicalExperience: selectionSet(() => TextMultipleLangItem, 1),
        timeSlots: selectionSet(() => TimeSlot, 1),
        meetingLink: 1,
        languageRequirementsDE: 1,
        languageRequirementsEN: 1,
        languageRequirementsOther: 1,
        additionalRequirements: selectionSet(() => TextMultipleLangItem, 1),
      })) as JobPosition | null;
      return result;
    }
  }, [props.jobPositionID, props.companyID]);

  const handleSave = () => {
    void XSnackbar.show('Stelle erfolgreich gespeichert.');
    // companyRouter is only mounted under /company/...; admins enter via /admin/job/... and
    // calling companyRouter.goTo here would throw "router not initialized". Send each role
    // back to the job's detail page so the user lands where they came from
    // (and, for freshly-created jobs, sees the new posting). Falls back to the
    // company overview if no _id is available yet — shouldn't happen post-save.
    const savedJobID = job.value?._id;
    if (window.location.pathname.startsWith('/admin/')) {
      if (savedJobID) {
        goTo({ path: `/admin/job/${props.companyID}/${savedJobID}` });
      } else {
        goTo({ path: `/admin/company/${props.companyID}` });
      }
    } else {
      if (savedJobID) {
        companyJobRouter.goTo('root', { params: { jobPositionID: savedJobID } });
      } else {
        companyRouter.goTo('root');
      }
    }
  };

  if (!job.value) return 'no job';

  const content = JobForm({ job: job.value, onsave: handleSave });

  return html`
    <div class="card bg-base-100 shadow">
      <div class="card-body">
        <h2 class="card-title">
          ${props.jobPositionID === 'new' ? m.job_form_create_title() : m.job_form_edit_title()}
        </h2>
        ${content}
      </div>
    </div>
  `;
});
